import { FormEvent, useMemo, useRef, useState } from 'react';

import { useModalFocusTrap } from '../../../accessibility/useModalFocusTrap';

import type {
  InvestigationClosureInputDto,
  InvestigationClosureOutcomeDto,
  MachineKnowledgeCandidateDto,
  MachineKnowledgeCandidatesResponseDto,
  MachineKnowledgeConfirmationDto,
  MachineKnowledgeTypeDto,
} from '../types/investigation';

export const INVESTIGATION_CLOSURE_OUTCOMES: Array<{
  id: InvestigationClosureOutcomeDto;
  title: string;
  description: string;
  icon: string;
}> = [
  { id: 'RESOLVED', title: 'Resolved', description: 'We understand the problem and the corrective action was completed.', icon: 'bi-check2-circle' },
  { id: 'HANDED_OFF', title: 'Handed off', description: 'The problem is understood, but work continues elsewhere.', icon: 'bi-arrow-right-circle' },
  { id: 'UNABLE_TO_CONFIRM', title: 'Unable to confirm', description: 'Available evidence was not enough to determine the cause.', icon: 'bi-question-circle' },
  { id: 'NO_LONGER_RELEVANT', title: 'No longer relevant', description: 'The issue no longer requires active Investigation.', icon: 'bi-dash-circle' },
];

const KNOWLEDGE_TYPE_LABELS: Record<MachineKnowledgeTypeDto, string> = {
  CONFIRMED_CAUSE: 'Confirmed cause',
  CONFIRMED_FINDING: 'Confirmed finding',
  CORRECTIVE_ACTION: 'Corrective action',
  VERIFIED_OUTCOME: 'Verified outcome',
  RULED_OUT_CAUSE: 'Ruled-out cause',
};

type CandidateReview = MachineKnowledgeCandidateDto & { selected: boolean; knowledge_type: MachineKnowledgeTypeDto };

export function CloseInvestigationDialog({
  busy = false,
  error = null,
  onClose,
  onReviewKnowledge,
  onSubmit,
}: {
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onReviewKnowledge: (closure: InvestigationClosureInputDto) => Promise<MachineKnowledgeCandidatesResponseDto>;
  onSubmit: (closure: InvestigationClosureInputDto) => Promise<void> | void;
}) {
  const [outcome, setOutcome] = useState<InvestigationClosureOutcomeDto | null>(null);
  const [whatHappened, setWhatHappened] = useState('');
  const [whatLearned, setWhatLearned] = useState('');
  const [whatWasDone, setWhatWasDone] = useState('');
  const [whatHappenedAfterward, setWhatHappenedAfterward] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [knowledgeReviewed, setKnowledgeReviewed] = useState(false);
  const [knowledgeCandidates, setKnowledgeCandidates] = useState<CandidateReview[]>([]);
  const [knowledgeBusy, setKnowledgeBusy] = useState(false);
  const [knowledgeError, setKnowledgeError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLFormElement>(null);
  useModalFocusTrap(dialogRef, true, busy || knowledgeBusy ? undefined : onClose);

  const requiresKnowledgeReview = outcome === 'RESOLVED' || outcome === 'HANDED_OFF';

  const closureDraft = useMemo<InvestigationClosureInputDto | null>(() => outcome ? ({
    outcome,
    what_happened: whatHappened.trim(),
    what_learned: whatLearned.trim(),
    what_was_done: whatWasDone.trim(),
    what_happened_afterward: whatHappenedAfterward.trim(),
    follow_up: followUp.trim(),
  }) : null, [followUp, outcome, whatHappened, whatHappenedAfterward, whatLearned, whatWasDone]);

  function invalidateKnowledgeReview() {
    setKnowledgeReviewed(false);
    setKnowledgeCandidates([]);
    setKnowledgeError(null);
  }

  function chooseOutcome(next: InvestigationClosureOutcomeDto) {
    setOutcome(next);
    setKnowledgeReviewed(false);
    setKnowledgeCandidates([]);
    setKnowledgeError(null);
  }

  async function reviewKnowledge() {
    if (!closureDraft || !closureDraft.what_happened || !requiresKnowledgeReview || knowledgeBusy) return;
    setKnowledgeBusy(true);
    setKnowledgeError(null);
    try {
      const result = await onReviewKnowledge(closureDraft);
      setKnowledgeCandidates(result.candidates.map((candidate) => ({
        ...candidate,
        selected: false,
        knowledge_type: candidate.suggested_type,
      })));
      setKnowledgeReviewed(true);
    } catch (value) {
      setKnowledgeReviewed(false);
      setKnowledgeError(value instanceof Error ? value.message : 'Route42 could not review the durable learning from this Investigation.');
    } finally {
      setKnowledgeBusy(false);
    }
  }

  function updateCandidate(candidateId: string, patch: Partial<CandidateReview>) {
    setKnowledgeCandidates((current) => current.map((candidate) => candidate.candidate_id === candidateId ? { ...candidate, ...patch } : candidate));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!closureDraft || !closureDraft.what_happened || busy || knowledgeBusy) return;
    if (requiresKnowledgeReview && !knowledgeReviewed) return;
    const confirmations: MachineKnowledgeConfirmationDto[] = knowledgeCandidates
      .filter((candidate) => candidate.selected && candidate.statement.trim())
      .map((candidate) => ({
        candidate_id: candidate.candidate_id,
        knowledge_type: candidate.knowledge_type,
        statement: candidate.statement.trim(),
      }));
    void onSubmit({
      ...closureDraft,
      ...(requiresKnowledgeReview ? {
        machine_knowledge_reviewed: true,
        machine_knowledge_confirmations: confirmations,
      } : {}),
    });
  }

  const canSubmit = Boolean(outcome && whatHappened.trim()) && (!requiresKnowledgeReview || knowledgeReviewed) && !busy && !knowledgeBusy;

  return (
    <div className="r42-dialog-layer" role="dialog" aria-modal="true" aria-labelledby="close-investigation-title" aria-describedby="close-investigation-description">
      <button className="r42-dialog-backdrop" type="button" tabIndex={-1} aria-label="Cancel closing investigation" onClick={onClose} disabled={busy || knowledgeBusy} />
      <form ref={dialogRef} tabIndex={-1} className="r42-dialog-card r42-investigation-close-dialog" onSubmit={submit}>
        <span className="r42-eyebrow">Close the loop without overstating certainty</span>
        <h2 id="close-investigation-title">How did this Investigation end?</h2>
        <p id="close-investigation-description">Choose the outcome that best reflects what the team actually knows. Closing an Investigation does not require a confirmed root cause.</p>

        {error ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Route42 couldn't close the Investigation.</strong><span>{error}</span></div></div> : null}

        <fieldset className="r42-investigation-closure-options">
          <legend>Outcome <b>Required</b></legend>
          {INVESTIGATION_CLOSURE_OUTCOMES.map((option) => (
            <label key={option.id} className={`r42-investigation-closure-option${outcome === option.id ? ' r42-investigation-closure-option--selected' : ''}`}>
              <input type="radio" name="closure-outcome" value={option.id} data-autofocus={option.id === 'RESOLVED' ? 'true' : undefined} aria-label={option.title} checked={outcome === option.id} onChange={() => chooseOutcome(option.id)} />
              <span className="r42-investigation-closure-option__icon" aria-hidden="true"><i className={`bi ${option.icon}`} /></span>
              <span><strong>{option.title}</strong><small>{option.description}</small></span>
            </label>
          ))}
        </fieldset>

        <div className="r42-investigation-dialog-fields r42-investigation-closure-fields">
          <label className="r42-field"><span className="r42-field__label">What happened? <b>Required</b></span><textarea rows={3} maxLength={2000} value={whatHappened} onChange={(event) => { setWhatHappened(event.target.value); invalidateKnowledgeReview(); }} placeholder="Summarize how this specific problem ended." /></label>
          <label className="r42-field"><span className="r42-field__label">What did we learn?</span><textarea rows={3} maxLength={2000} value={whatLearned} onChange={(event) => { setWhatLearned(event.target.value); invalidateKnowledgeReview(); }} placeholder="Capture the useful learning, including uncertainty that remains." /></label>
          <label className="r42-field"><span className="r42-field__label">What was done?</span><textarea rows={3} maxLength={2000} value={whatWasDone} onChange={(event) => { setWhatWasDone(event.target.value); invalidateKnowledgeReview(); }} placeholder="Record corrective action, inspection, escalation, or handoff." /></label>
          <label className="r42-field"><span className="r42-field__label">What happened afterward?</span><textarea rows={3} maxLength={2000} value={whatHappenedAfterward} onChange={(event) => { setWhatHappenedAfterward(event.target.value); invalidateKnowledgeReview(); }} placeholder="Record the result after the action, if known." /></label>
          <label className="r42-field"><span className="r42-field__label">Anything worth following separately?</span><textarea rows={2} maxLength={2000} value={followUp} onChange={(event) => setFollowUp(event.target.value)} placeholder="Add a follow-up that should not keep this Investigation open." /></label>
        </div>

        {requiresKnowledgeReview ? <section className="r42-investigation-knowledge-review" aria-labelledby="machine-knowledge-review-title">
          <div className="r42-investigation-knowledge-review__head">
            <div><span className="r42-eyebrow">Machine Knowledge</span><h3 id="machine-knowledge-review-title">What Route42 will remember about this machine</h3></div>
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!whatHappened.trim() || busy || knowledgeBusy} onClick={() => void reviewKnowledge()}>{knowledgeBusy ? 'Reviewing…' : knowledgeReviewed ? 'Review again' : 'Review learning'}</button>
          </div>
          <p>Route42 may structure durable learnings from the closure and confirmed Investigation findings, but nothing becomes Machine Knowledge until you explicitly select it here.</p>
          {knowledgeError ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Knowledge review failed.</strong><span>{knowledgeError}</span></div></div> : null}
          {!knowledgeReviewed ? <div className="r42-investigation-knowledge-review__pending">Review the proposed learning before closing this Investigation.</div> : knowledgeCandidates.length === 0 ? <div className="r42-investigation-knowledge-review__empty"><i className="bi bi-shield-check" aria-hidden="true" /><span>Nothing from this closure currently meets the promotion threshold. You can close the Investigation without adding Machine Knowledge.</span></div> : <div className="r42-investigation-knowledge-candidates">
            {knowledgeCandidates.map((candidate) => (
              <article key={candidate.candidate_id} className={`r42-investigation-knowledge-candidate${candidate.selected ? ' r42-investigation-knowledge-candidate--selected' : ''}`}>
                <label className="r42-investigation-knowledge-candidate__check"><input type="checkbox" checked={candidate.selected} onChange={(event) => updateCandidate(candidate.candidate_id, { selected: event.target.checked })} /><span>Remember this</span></label>
                <label className="r42-field"><span className="r42-field__label">Knowledge type</span><select value={candidate.knowledge_type} disabled={!candidate.selected} onChange={(event) => updateCandidate(candidate.candidate_id, { knowledge_type: event.target.value as MachineKnowledgeTypeDto })}>{candidate.allowed_types.map((type) => <option key={type} value={type}>{KNOWLEDGE_TYPE_LABELS[type]}</option>)}</select></label>
                <label className="r42-field"><span className="r42-field__label">Durable learning</span><textarea rows={2} maxLength={2000} value={candidate.statement} disabled={!candidate.selected} onChange={(event) => updateCandidate(candidate.candidate_id, { statement: event.target.value })} /></label>
                <small>{candidate.supporting_evidence.length ? `${candidate.supporting_evidence.length} supporting evidence item${candidate.supporting_evidence.length === 1 ? '' : 's'}` : 'Established from the Investigation closure record.'}</small>
              </article>
            ))}
          </div>}
        </section> : null}

        <div className="r42-investigation-closure-note"><i className="bi bi-shield-check" aria-hidden="true" /><span>Route42 preserves the ending as part of the Investigation record. Machine Knowledge remains downstream: AI can structure a learning, but only your explicit confirmation can promote it.</span></div>
        <div className="r42-dialog-actions"><button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy || knowledgeBusy}>Keep investigation open</button><button type="submit" className="btn r42-btn-primary" disabled={!canSubmit}>{busy ? 'Closing…' : 'Close investigation'}</button></div>
      </form>
    </div>
  );
}
